import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useParams } from "react-router-dom";
import { useMenuItems } from "../queries/menuQueries";
import { useCartStore } from "../stores/cartStore";
import { formatPrice } from "../utils/currency";
import {
  Container,
  Box,
  Text,
  Button,
  Group,
  Stack,
  Center,
  Alert,
  Image,
  Divider,
  Skeleton,
} from "@mantine/core";
import { IconAlertCircle, IconShoppingCart } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
// import transparentPizza from "../assets/transparent_pizza_v2.png";
import transparentPizza from "../assets/pollo-al-pesto-pizza-top-view--t3chat--1-nobg.png";
// import proceccioPizza from "../assets/proceccio_pizza.png";
import proceccioPizza from "../assets/bismark-no-background.png";

// Temporary image array - in the future, each menu item will have its own image_url
const menuImages = [transparentPizza, proceccioPizza];

export default function MenuPageV2() {
  const { t } = useTranslation();
  const { restaurant } = useParams<{ restaurant: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: menuItems, isLoading, error } = useMenuItems(true, restaurant);
  const addToCart = useCartStore((state) => state.addToCart);

  // Initialize selectedCategory from URL parameter
  const categoryParam = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categoryParam || "All"
  );

  // Update selectedCategory when URL parameter changes
  useEffect(() => {
    const category = searchParams.get("category");
    if (category) {
      setSelectedCategory(category);
    } else {
      setSelectedCategory("All");
    }
  }, [searchParams]);

  const handleAddToCart = (item: any) => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      image_url: item.image_url,
    });
    notifications.show({
      id: "add-to-cart-notification",
      title: t("menu.addToCart"),
      message: item.name,
      color: "teal",
      autoClose: 3000,
    });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Update URL parameter
    if (category === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ category: category });
    }
  };

  if (isLoading) {
    return (
      <Box
        style={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Container size="md" py="xl" px="lg">
          {/* Category filters skeleton */}
          <Group mb="xl" gap="xs">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={32} width={80} radius="xl" />
            ))}
          </Group>

          {/* Menu items skeleton */}
          <Stack gap={0}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Box key={i}>
                <Box style={{ padding: "24px 0" }}>
                  <Group wrap="nowrap" gap="md" align="flex-start">
                    {/* Image skeleton */}
                    <Skeleton height={120} width={120} radius="sm" />

                    {/* Content skeleton */}
                    <Box style={{ flex: 1 }}>
                      {/* Title */}
                      <Skeleton height={24} width="60%" mb="xs" />

                      {/* Description */}
                      <Skeleton height={16} width="90%" mb={8} />
                      <Skeleton height={16} width="70%" mb="md" />

                      {/* Price and Button */}
                      <Group justify="space-between" align="center">
                        <Skeleton height={24} width={80} />
                        <Skeleton height={42} width={100} radius="xl" />
                      </Group>
                    </Box>
                  </Group>
                </Box>
                {i < 5 && <Divider color="gray.2" />}
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title={t("menu.error")}
        >
          {t("menu.errorDescription")}
        </Alert>
      </Container>
    );
  }

  // Get unique categories
  const categories = [
    "All",
    ...Array.from(new Set(menuItems?.map((item) => item.category) || [])),
  ];

  // Filter items by category
  const filteredItems =
    selectedCategory === "All"
      ? menuItems
      : menuItems?.filter((item) => item.category === selectedCategory);

  const getImageSrc = (item: any, index: number) => {
    if (!item.menu_number) return null;
    return item.image_url || menuImages[index % menuImages.length];
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        // backgroundColor: "#f8f4f4",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Container size="md" py="xl" px="lg">
        {/* Category filters */}
        <Group mb="xl" gap="xs">
          {categories.map((category) => (
            <Button
              key={category}
              data-testid={`category-filter-${category.toLowerCase()}`}
              variant={selectedCategory === category ? "filled" : "light"}
              color={selectedCategory === category ? "dark" : "gray"}
              size="sm"
              onClick={() => handleCategoryChange(category)}
              styles={{
                root: {
                  borderRadius: "20px",
                  textTransform: "uppercase",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                },
              }}
            >
              {category === "All" ? t("menu.categoryAll") : category}
            </Button>
          ))}
        </Group>

        {/* Menu items list */}
        {filteredItems && filteredItems.length > 0 ? (
          <Stack gap={0}>
            {filteredItems.map((item, index) => {
              const imageSrc = getImageSrc(item, index);
              return (
                <Box key={item.id} data-testid="menu-item">
                  <Box
                    style={{
                      padding: "24px 0",
                      cursor: "pointer",
                    }}
                  >
                    <Group wrap="nowrap" gap="md" align="flex-start">
                      {/* Image section */}
                      {imageSrc && (
                        <Box
                          style={{
                            width: "120px",
                            height: "120px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Image
                            src={imageSrc}
                            alt={item.name}
                            w="100%"
                            h="100%"
                            fit="cover"
                            loading="lazy"
                          />
                        </Box>
                      )}

                      {/* Content section */}
                      <Box
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {/* Item name */}
                        <Text
                          fw={700}
                          size="lg"
                          tt="uppercase"
                          c="dark.9"
                          style={{
                            letterSpacing: "0.5px",
                            lineHeight: 1.2,
                          }}
                        >
                          {item.menu_number ? `${item.menu_number}. ` : ""}
                          {item.name}
                        </Text>

                        {/* Description */}
                        {item.description && (
                          <Text
                            size="sm"
                            c="gray.6"
                            lineClamp={2}
                            style={{ lineHeight: 1.4 }}
                          >
                            {item.description}
                          </Text>
                        )}

                        {/* Price and Add button - Fixed layout */}
                        <Group justify="space-between" mt="xs" wrap="nowrap">
                          <Text
                            size="lg"
                            fw={700}
                            c="dark.9"
                            style={{ minWidth: "80px" }}
                          >
                            {formatPrice(item.price)}
                          </Text>
                          <Button
                            data-testid={`add-to-cart-${item.id}`}
                            size="md"
                            radius="xl"
                            leftSection={<IconShoppingCart size={18} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(item);
                            }}
                            style={{
                              flexShrink: 0,
                              backgroundColor:
                                "var(--mantine-color-neutral-10)",
                              color: "var(--mantine-color-dark-9)",
                              border:
                                "1px solid var(--mantine-color-neutral-3)",
                            }}
                          >
                            Tilføj
                          </Button>
                        </Group>
                      </Box>
                    </Group>
                  </Box>
                  {index < filteredItems.length - 1 && (
                    <Divider color="gray.2" />
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Center h={200}>
            <Text c="dimmed">{t("menu.empty")}</Text>
          </Center>
        )}
      </Container>
    </Box>
  );
}
